import React, { useState, useEffect } from 'react';

/**
 * i18n
 */
import './common/serviecs/i18n';
import { useTranslation } from 'react-i18next';

/**
 * React Router
 */
import { Switch, Route, useHistory } from "react-router-dom";

/**
 * Material UI
 */
import { makeStyles, createMuiTheme, ThemeProvider } from '@material-ui/core/styles';
import { CssBaseline, Container, Box, responsiveFontSizes, Typography, Backdrop } from '@material-ui/core';

/**
 * Firebase
 */
import firebase, { firestore, getFirestoreTimestamp, addHoursToFirestoreTimestamp } from './common/serviecs/firebase';

/**
 * Application
 */
import { MENU_ITEMS, CUSTOM_THEME, overrdieMuiDataTableTheme, USER_ROLES, USER_ROLE_PILOT, USER_ROLE_PILOT_EXPIRY_THRESHOLD_IN_DAYS } from './app/Constants';
import { Home } from './app/components/Home';
import { MainAppBar } from './common/components/controls/MainAppBar';
import { LoadingMask } from './common/components/controls/LoadingMask';
import { UserSelectionPanel } from './common/components/controls/UserSelection';
import { ErrorBoundary } from './common/components/controls/ErrorBoundary';
import './Print.css';
import { Dashboard } from './app/dashboard/Dashboard';
import ImageEditingDialog from './lib/slate/plugins/Media';
import { GoogleLoginButton } from './common/components/controls';
import { PilotExpiryBackdrop } from './app/components/PilotExpiryBackdrop';
import { MessageDialog } from './common/components/controls/MessageDialog';

/**
 * Create a new Context to storage
 * 1. loggedInGoogleUser
 * 2. selectedUser
 * 3. setUser method
 */
export const UserContext = React.createContext();
export const NavigationContext = React.createContext();
export const AppContext = React.createContext();

export function App(props) {

	/**
	 * Override Theme
	 */
	var theme = responsiveFontSizes(
		createMuiTheme(Object.assign({}, CUSTOM_THEME)));
	theme = {
		...theme,
		...overrdieMuiDataTableTheme(theme)
	};

	const classes = makeStyles(theme => ({
		root: {
			width: "100%",
			color: "rgb(44,62,80)",
			fontFamily: "Calibri",
			textAlign: "center",
		},
		rootContainer: {
			padding: 0,
			display: "table",
		},
	}))();

	{/* i18t */ }
	const { t, i18n } = useTranslation();

	// For menu item routing
	const history = useHistory();
	const goto = (path) => {
		history.push(path);
	}

	{/* Logged In User */ }
	const [account, setAccount] = useState();
	const accountId = Boolean(account) ? account.id : null;
	{/* Selected User */ }
	// const [user, setUser] = useState(null);
	const [user, setUser] = useState(null);
	const selectUser = (user) => {
		// Store in local storage the selected account
		localStorage.setItem("selectedUserId", user.id);
		setUser(user);
	}

	{/* General Warning Message */ }
	const [message, setMessage] = useState();

	/**
	 * 2020-05-08: New structure of account and user
	 * 1. Both owner and user aer document under collection users with different attributes
	 * 2. The email address would be used as the id of the document.
	 * 3. If the user has no email address, an auto-generated ID would be used.
	 *    But this would create problem when user provide a email address later. [TODO]
	 * 4. The type attribute will identify whether it is owner/parent/child
	 * 5. Non-owner user would be an additional attribute called "ownerUserId" to identify its owner
	 * 6. They will have their own notification objects
	 * 7. Owner might have a sub-collection called "service-plans" [TODO]
	 */
	useEffect(() => {
		firebase.auth().onAuthStateChanged((firebaseUser) => {
			if (Boolean(firebaseUser)) {
				/**
				 * Get all the details from the firebase user object.
				 * Current only Google Login is supported
				 */
				var names = firebaseUser.displayName.split(" ");
				var loggedInGoogleAccount = {
					uid: firebaseUser.uid,
					fullName: firebaseUser.displayName,
					firstName: names[0],
					lastName: names[names.length - 1],
					googleImageUrl: firebaseUser.photoURL,
					email: firebaseUser.email,
					phoneNumber: firebaseUser.phoneNumber,
					lastLoggedInAt: getFirestoreTimestamp(),
					// No need to store tokens
					// idToken: firebaseUser.xa,
					idToken: firebaseUser._lat,
					// refreshToken: firebaseUser.refreshToken
				}
				firestore.collection("users").doc(loggedInGoogleAccount.email).get().then(loggedInUserRef => {
					if (loggedInUserRef.exists) {
						/**
						 * If user already exist:
						 * 1. Find the logged in user
						 * 2. Select the last selected delegate user (of the owner user) from this account as current user if any
						 */
						let loggedInUser = Object.assign({ id: loggedInUserRef.id, imageUrl: loggedInGoogleAccount.googleImageUrl }, loggedInUserRef.data(), loggedInGoogleAccount);
						firestore.collection("users").doc(loggedInGoogleAccount.email).set(loggedInUser);
						setAccount(loggedInUser);

						// 2. Select the last selected user from this account as current user
						var selectedUserId = localStorage.getItem("selectedUserId");
						if (Boolean(selectedUserId) && selectedUserId === loggedInUser.id) {
							setUser(loggedInUser);
						} else {
							var otherUsers = [];
							firestore.collection("users").where("ownerUserId", "==", loggedInUser.ownerUserId).get().then(usersRef => {
								usersRef.forEach(userRef => {
									let otherUser = Object.assign({ id: userRef.id }, userRef.data());
									// Check if there is any other users w/o email and entitled to use
									if (otherUser.id !== loggedInUser.id &&
										!Boolean(otherUser.email) &&
										USER_ROLES[loggedInUser.role].entitledRoles.includes(otherUser.role)) {
										otherUsers.push(otherUser);
									}
								});
								console.log(otherUsers)
								console.log(otherUsers.length);
								if (Boolean(selectedUserId)) {
									setUser(otherUsers.filter(u => (u.id === selectedUserId))[0]);
								} else if (otherUsers.length === 0) {
									// It it is the only entitled user
									setUser(loggedInUser);
								}
							});
						}
					} else {
						/**
						 * If user does not exist
						 * 1. Create an owner account for the user
						 * 2. Create an owner user under this owner account
						 */
						let currentTimestamp = getFirestoreTimestamp();
						let newLoggedInUser = Object.assign(
							{
								role: USER_ROLE_PILOT,
								ownerUserId: loggedInGoogleAccount.email,
								createdAt: currentTimestamp,
								pilotExpiresAt: addHoursToFirestoreTimestamp(currentTimestamp, USER_ROLE_PILOT_EXPIRY_THRESHOLD_IN_DAYS * 24),
								imageUrl: loggedInGoogleAccount.googleImageUrl,
							},
							loggedInGoogleAccount);
						firestore.collection("users").doc(loggedInGoogleAccount.email).set(newLoggedInUser);
						// Make the logged in user as selected user
						setAccount(newLoggedInUser);
						selectUser(Object.assign({ id: loggedInGoogleAccount.email }, newLoggedInUser));
					}
				});
			} else {
				/**
				 * Handle logout
				 */
				setAccount(null);
				setUser(null);
				localStorage.clear();
			}
		});
	}, [accountId])

	return (
		<ThemeProvider theme={theme}>
			<ErrorBoundary>
				<AppContext.Provider value={{ setMessage: setMessage }}>
					<UserContext.Provider value={{ account: account, setAccount: setAccount, user: user, setUser: setUser }}>
						<NavigationContext.Provider value={{ goto: goto }}>
							<CssBaseline />
							{/* Pilot Expiry Message if Applicable */}
							{Boolean(user) && Boolean(user?.role === USER_ROLE_PILOT) && Boolean(user?.pilotExpiresAt?.seconds < getFirestoreTimestamp().seconds) &&
								<PilotExpiryBackdrop />}
							{/* Pilot Expiry Message if Applicable */}
							<Box displayPrint="none">
								<MainAppBar
									t={t}
									i18n={i18n}
									account={account}
									user={user}
									goto={goto}
									selectUser={selectUser} />
							</Box>
							<Container className={classes.rootContainer} maxWidth="xl">
								{/* General Warning Message Dialog */}
								{Boolean(message) ? <MessageDialog open={true} title="Test Paper Online" message={message} closeCallback={() => setMessage(null)} /> : null}
								{/* End: General Warning Message Dialog */}
								{/* Temp */}
								<ImageEditingDialog open={false} />
								{/* User Selection Panel */}
								{Boolean(account) && !Boolean(user) &&
									<UserSelectionPanel account={account} onSelectUser={selectUser} />}
								{/* End: User Selection Panel */}
								<Switch>
									{MENU_ITEMS.filter(mi => Boolean(mi.path)).map(mi =>
										<Route key={mi.key} path={mi.path}
											render={(props) => (!mi.requireAuthentication || Boolean(user)) ? <mi.component {...props} {...mi.props || {}} t={t} account={account} user={user} goto={goto} /> :
												<Box textAlign="center">
													{false && <LoadingMask message="Authenicating" />}
													<Box displayPrint="none" py={5}>
														<Box my={2}>
															<GoogleLoginButton />
														</Box>
													</Box>
												</Box>} />)}
									<Route path="/">
										{Boolean(account) && Boolean(user) ?
											// Show Dashboard directly once logged in
											Boolean(user.email === 'glycheung@gmail.com----') ?
												<Box textAlign="center" mt={25}>
													<Typography>This site has been shut down.</Typography>
													<Typography>Years worth of effort is proven to be a waste of time.</Typography>
													<Typography>Thank you very much for the ones who have used and supported the site.</Typography>
													<Typography>Goodbye!!!</Typography>
												</Box> :
												<Dashboard
													t={t}
													account={account}
													user={user}
													goto={goto} />
											:
											// Show homepage for guest user
											<Home
												t={t}
												account={account}
												user={user}
												goto={goto} />}
									</Route>
								</Switch>
							</Container>
						</NavigationContext.Provider>
					</UserContext.Provider>
				</AppContext.Provider>
			</ErrorBoundary >
		</ThemeProvider>
	);
}
